import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { keepPreviousData, useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { BrandLockup, BrandMark } from "./Brand.tsx";
import {
  Button as AriaButton,
  Dialog,
  Header,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Modal,
  ModalOverlay,
  Popover,
  Separator,
  ToggleButton,
  ToggleButtonGroup,
  type Key,
} from "react-aria-components";
import {
  BuildingsIcon,
  CaretRightIcon,
  CaretUpDownIcon,
  ChartLineUpIcon,
  CheckIcon,
  ClockCounterClockwiseIcon,
  DesktopIcon,
  KeyboardIcon,
  ListIcon,
  LockSimpleIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  PlusIcon,
  RocketLaunchIcon,
  RowsIcon,
  SignOutIcon,
  SunIcon,
  TranslateIcon,
} from "@phosphor-icons/react";
import type { Permission } from "@bres/domain";
import { Avatar, Button, CommandPalette, Dialog as UiDialog, Kbd, Mark, PageMarkProvider, Toaster, Tooltip, type CommandItem } from "@bres/ui";
import { api, qs } from "../lib/api.ts";
import { UI_LOCALES, useI18n } from "../lib/i18n.tsx";
import { useOverview } from "../lib/overview.ts";
import { useIsPlatformAdmin, useSessionState, type MemberInfo } from "../lib/session.tsx";
import { date } from "../lib/format.ts";
import { NAV, platformNav, isPlatformPath, navItemFor, type NavItem } from "./nav.tsx";
import { readRecent } from "../lib/recent.ts";
import { useAccessRequest, useClientDirectory, switchToTenant, type ClientEntry } from "./clients.tsx";
import { applyDensity, applyTheme, effectiveTheme, readDensity, readTheme, type Density, type ThemeChoice } from "./theme.ts";

/** Actief is het navigatie-item met de langste overeenkomst (zodat /platform niet ook bij /platform/sites oplicht). */
function useIsActive() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const active = navItemFor(path)?.to;
  return (to: string) => to === active;
}

const isTypingTarget = (el: EventTarget | null) => el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.closest("[role=dialog]") !== null);

/** Huidige modus: het platform (BRES zelf) of één klant. Volgt uit de route. */
export function usePlatformMode(): boolean {
  return isPlatformPath(useRouterState({ select: (s) => s.location.pathname }));
}

/**
 * Wisselaar bovenin de zijbalk: platform of een klant. Een platformbeheerder ziet het platform bovenaan en daaronder
 * alle klanten; bij een klant zonder lidmaatschap neemt hij eerst zichtbaar en tijdelijk toegang (ADR 0007).
 */
function ContextSwitch({ onNavigate }: { onNavigate?: () => void }) {
  const { t, tag } = useI18n();
  const { me, member } = useSessionState();
  const platformAdmin = useIsPlatformAdmin();
  const platformMode = usePlatformMode();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { clients } = useClientDirectory();
  const access = useAccessRequest();
  const currentTenant = me.tenants?.find((x) => x.id === member?.tenantId);
  const options = clients.length + (platformAdmin ? 1 : 0);
  const showPlatform = platformMode || !member;

  const onAction = async (key: Key) => {
    const id = String(key);
    onNavigate?.();
    if (id === "platform") return void navigate({ to: "/platform" });
    if (id === member?.tenantId) return platformMode ? void navigate({ to: "/" }) : undefined;
    const client = clients.find((x) => x.id === id);
    if (client && !client.membership) return access.request(client);
    await switchToTenant(id, qc);
  };

  const title = showPlatform ? t("shell.platform_name") : member.tenantName;
  const subtitle = showPlatform ? t("shell.platform_role") : `${t(`role.${member.role}`)}${currentTenant?.is_demo ? ` · ${t("shell.demo")}` : ""}`;

  return (
    <div className="nav-panel__top">
      <MenuTrigger>
        <AriaButton className="org-switch" aria-label={`${t("shell.switch_org")}: ${title}`} isDisabled={options < 2}>
          <span className={`org-switch__mark${showPlatform ? " org-switch__mark--platform" : ""}`} aria-hidden>
            {showPlatform ? <BresMark /> : member.tenantName.slice(0, 1)}
          </span>
          <span className="org-switch__text">
            <span className="org-switch__name truncate">{title}</span>
            <span className="org-switch__role truncate">{subtitle}</span>
          </span>
          {options > 1 ? <CaretUpDownIcon aria-hidden /> : null}
        </AriaButton>
        <Popover className="ui-popover org-switch__popover" placement="bottom start">
          <Menu className="ui-menu org-switch__menu" aria-label={t("shell.switch_org")} onAction={(k) => void onAction(k)}>
            {platformAdmin ? (
              <MenuSection>
                <Header className="ui-menu-section-head">{t("shell.platform_label")}</Header>
                <MenuItem id="platform" textValue={t("shell.platform_name")} className="ui-menu-item">
                  <Mark icon={<ChartLineUpIcon />} hue="sun" size="1.5rem" />
                  <span>{t("shell.platform_name")}</span>
                  {platformMode ? <CheckIcon aria-hidden className="menu-check" /> : null}
                </MenuItem>
              </MenuSection>
            ) : null}
            {clients.length ? (
              <MenuSection>
                <Header className="ui-menu-section-head">{t(platformAdmin ? "shell.clients_label" : "shell.org_label")}</Header>
                {clients.map((x) => (
                  <MenuItem key={x.id} id={x.id} textValue={x.name} className={`ui-menu-item${x.membership ? "" : " org-switch__locked"}`}>
                    <span className="org-switch__mini" style={{ background: `var(--series-${x.color})` }} aria-hidden>
                      {x.name.slice(0, 1)}
                    </span>
                    <span className="org-switch__item">
                      <span className="truncate">{x.name}</span>
                      {!x.membership ? (
                        <span className="org-switch__item-meta">{t("access.none")}</span>
                      ) : x.membership.is_support && x.membership.expires_at ? (
                        <span className="org-switch__item-meta">{t("shell.support_until", { date: date(x.membership.expires_at, tag) })}</span>
                      ) : null}
                    </span>
                    {!x.membership ? <LockSimpleIcon aria-hidden className="org-switch__lock" /> : null}
                    {!platformMode && x.id === member?.tenantId ? <CheckIcon aria-hidden className="menu-check" /> : null}
                  </MenuItem>
                ))}
              </MenuSection>
            ) : null}
          </Menu>
        </Popover>
      </MenuTrigger>
      {access.dialog}
      {!platformMode && member?.isSupport ? <p className="nav-panel__support">{currentTenant?.expires_at ? t("shell.support_access", { date: date(currentTenant.expires_at, tag) }) : t("shell.support_access_open")}</p> : null}
    </div>
  );
}

/** Merk van BRES voor het platform (tijdelijk een letter, tot het logo van de BRES-website er is). */
function BresMark() {
  return <BrandMark className="org-switch__brand" />;
}

/** Platformmodus: alle klanten, direct te openen vanuit de zijbalk (zonder toegang eerst toegang nemen). */
function ClientLinks({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { clients } = useClientDirectory();
  const access = useAccessRequest();
  if (!clients.length) return null;
  const open = async (client: ClientEntry) => {
    if (!client.membership) return access.request(client);
    onNavigate?.();
    await switchToTenant(client.id, qc);
  };
  return (
    <div className="nav-group">
      <div className="nav-group__label">{t("shell.clients_label")}</div>
      {clients.map((x) => (
        <AriaButton key={x.id} className={`nav-link nav-link--client${x.membership ? "" : " nav-link--locked"}`} onPress={() => void open(x)} aria-label={x.membership ? x.name : `${x.name}: ${t("access.none")}`}>
          <span className="nav-link__initial" style={{ background: `var(--series-${x.color})` }} aria-hidden>
            {x.name.slice(0, 1)}
          </span>
          <span className="truncate">{x.name}</span>
          {x.membership ? <CaretRightIcon className="nav-link__go" aria-hidden /> : <LockSimpleIcon className="nav-link__go nav-link__lock" aria-hidden />}
        </AriaButton>
      ))}
      {access.dialog}
    </div>
  );
}

/** Zijbalk: één witte pil glijdt met een veer naar het actieve item (eerste plaatsing zonder animatie). */
function NavPanel({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useI18n();
  const { me, member } = useSessionState();
  const platformMode = usePlatformMode();
  const overview = useOverview(!platformMode && Boolean(member));
  const isActive = useIsActive();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const counts = overview.data?.counts ?? {};
  const scrollRef = useRef<HTMLElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const groups = platformMode || !member ? platformNav(Boolean(me.tenants?.some((x) => x.slug === "bres"))) : NAV.map((g) => ({ ...g, items: g.items.filter((i) => i.permission && member.permissions.includes(i.permission)) }));

  const place = useCallback(() => {
    const nav = scrollRef.current;
    const pill = indicatorRef.current;
    if (!nav || !pill) return;
    const active = nav.querySelector<HTMLElement>(".nav-link.is-active");
    if (!active) {
      pill.style.opacity = "0";
      return;
    }
    const a = active.getBoundingClientRect();
    const n = nav.getBoundingClientRect();
    pill.style.opacity = "1";
    pill.style.setProperty("--y", `${a.top - n.top + nav.scrollTop}px`);
    pill.style.setProperty("--h", `${a.height}px`);
    if (!pill.dataset.ready) requestAnimationFrame(() => (pill.dataset.ready = "true"));
  }, []);

  useLayoutEffect(place, [place, path, overview.data, platformMode]);
  useEffect(() => {
    const nav = scrollRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(place);
    ro.observe(nav);
    return () => ro.disconnect();
  }, [place]);

  return (
    <div className="nav-panel">
      <div className="nav-panel__brand">
        <BrandLockup />
      </div>
      <ContextSwitch onNavigate={onNavigate} />
      <nav className="nav-panel__scroll" aria-label={t("nav.label")} ref={scrollRef}>
        <span className="nav-indicator" ref={indicatorRef} aria-hidden />
        {groups.map((group) => {
          if (!group.items.length) return null;
          return (
            <div className="nav-group" key={group.labelKey}>
              <div className="nav-group__label">{t(group.labelKey)}</div>
              {group.items.map((item) => {
                const count = item.countKey ? (counts[item.countKey] ?? 0) : 0;
                const active = isActive(item.to);
                return (
                  <Link key={item.to} to={item.to} className={`nav-link${active ? " is-active" : ""}`} aria-current={active ? "page" : undefined} onClick={onNavigate}>
                    <Mark icon={item.icon} hue={item.hue} solid={active} className="nav-link__mark" />
                    <span>{t(item.labelKey)}</span>
                    {count > 0 ? <span className={`nav-link__count${item.quietCount ? " nav-link__count--quiet" : ""}`}>{count}</span> : null}
                  </Link>
                );
              })}
            </div>
          );
        })}
        {platformMode || !member ? <ClientLinks onNavigate={onNavigate} /> : null}
      </nav>
      <div className="nav-panel__bottom">
        <UserMenu />
      </div>
    </div>
  );
}

function UserMenu() {
  const { t, locale, setLocale } = useI18n();
  const { me } = useSessionState();
  const user = me.user ?? { name: "", email: "" };
  const qc = useQueryClient();
  const [density, setDensity] = useState<Density>(readDensity);
  const onAction = async (key: Key) => {
    const k = String(key);
    if (k.startsWith("lang:")) setLocale(k.slice(5) as "nl" | "en");
    else if (k.startsWith("density:")) {
      const next = k.slice(8) as Density;
      applyDensity(next);
      setDensity(next);
    } else if (k === "signout") {
      await api.post("/v1/auth/sign-out", {}).catch(() => undefined);
      qc.clear();
      window.location.assign("/");
    }
  };
  return (
    <MenuTrigger>
      <AriaButton className="user-button" aria-label={t("shell.user_menu")}>
        <Avatar name={user.name} />
        <span className="user-button__text">
          <span className="truncate">{user.name}</span>
          <span className="user-button__email truncate">{user.email}</span>
        </span>
        <CaretUpDownIcon aria-hidden />
      </AriaButton>
      <Popover className="ui-popover" placement="top start">
        <Menu className="ui-menu" aria-label={t("shell.user_menu")} onAction={(k) => void onAction(k)}>
          <MenuSection>
            <Header className="ui-menu-section-head">{t("shell.language")}</Header>
            {UI_LOCALES.map((l) => (
              <MenuItem key={l.id} id={`lang:${l.id}`} textValue={l.label} className="ui-menu-item">
                <TranslateIcon aria-hidden />
                <span lang={l.id}>{l.label}</span>
                {locale === l.id ? <CheckIcon aria-hidden className="menu-check" /> : null}
              </MenuItem>
            ))}
          </MenuSection>
          <Separator className="ui-separator" />
          <MenuSection>
            <Header className="ui-menu-section-head">{t("shell.density")}</Header>
            {(["comfortable", "compact"] as const).map((x) => (
              <MenuItem key={x} id={`density:${x}`} textValue={t(`shell.density_${x}`)} className="ui-menu-item">
                <RowsIcon aria-hidden />
                <span>{t(`shell.density_${x}`)}</span>
                {density === x ? <CheckIcon aria-hidden className="menu-check" /> : null}
              </MenuItem>
            ))}
          </MenuSection>
          <Separator className="ui-separator" />
          <MenuItem id="signout" textValue={t("shell.sign_out")} className="ui-menu-item">
            <SignOutIcon aria-hidden />
            <span>{t("shell.sign_out")}</span>
          </MenuItem>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

/** Eén rond knopje dat bij aanwijzen of focus uitklapt naar drie keuzes. */
function ThemeSwitch() {
  const { t } = useI18n();
  const [theme, setTheme] = useState<ThemeChoice>(readTheme);
  useEffect(() => {
    const on = (e: Event) => setTheme((e as CustomEvent<ThemeChoice>).detail);
    window.addEventListener("bres:theme", on);
    return () => window.removeEventListener("bres:theme", on);
  }, []);
  const icons: Record<ThemeChoice, ReactNode> = { system: <DesktopIcon aria-hidden />, light: <SunIcon aria-hidden />, dark: <MoonIcon aria-hidden /> };
  return (
    <ToggleButtonGroup
      className="theme-switch"
      aria-label={t("shell.theme_switch")}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[theme]}
      onSelectionChange={(keys) => {
        const next = [...keys][0] as ThemeChoice | undefined;
        if (next) applyTheme(next);
      }}
    >
      {(["system", "light", "dark"] as const).map((x) => (
        <ToggleButton key={x} id={x} className="theme-switch__btn" aria-label={t(`shell.theme_${x}`)}>
          {icons[x]}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/** Alle bereikbare schermen: klantschermen volgens rechten, plus het platform voor platformbeheerders. */
function availableNav(member: MemberInfo | null, platformAdmin: boolean, hasBresMembership: boolean): NavItem[] {
  const tenant = member ? NAV.flatMap((g) => g.items).filter((i) => i.permission && member.permissions.includes(i.permission)) : [];
  return [...tenant, ...(platformAdmin ? platformNav(hasBresMembership).flatMap((g) => g.items) : [])];
}

function CommandMenu({ isOpen, onOpenChange }: { isOpen: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useI18n();
  const { me, member } = useSessionState();
  const platformAdmin = useIsPlatformAdmin();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const term = input.trim();
  const props = useQuery({
    queryKey: ["command-properties", term],
    queryFn: () => api.get<{ items: { id: string; reference: string; title: string | null; locality: string | null }[] }>(`/v1/properties${qs({ q: term || undefined, limit: 8 })}`),
    enabled: isOpen && Boolean(member),
    staleTime: 30_000,
    // Vorige treffers blijven staan tot de nieuwe binnen zijn: geen lege lijst tussen twee toetsaanslagen.
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    if (!isOpen) setInput("");
  }, [isOpen]);
  const recent = useMemo(() => (isOpen ? readRecent() : []), [isOpen]);
  const items = useMemo<CommandItem[]>(() => {
    const seen = new Set((props.data?.items ?? []).map((p) => p.id));
    const recents: CommandItem[] = term
      ? []
      : recent
          .filter((r) => !seen.has(r.id))
          .map((r) => ({ id: `property:${r.id}`, label: `${r.reference}  ${r.title}`, keywords: `${r.reference} ${r.title}`, icon: <ClockCounterClockwiseIcon aria-hidden />, group: t("shell.command_group_recent") }));
    const pages: CommandItem[] = availableNav(member, platformAdmin, Boolean(me.tenants?.some((x) => x.slug === "bres"))).map((i) => ({
      id: `go:${i.to}`,
      label: t(i.labelKey),
      icon: i.icon,
      hint: i.shortcut ? `G ${i.shortcut.toUpperCase()}` : undefined,
      group: t(i.permission ? "shell.command_group_pages" : "shell.command_group_platform"),
    }));
    const found: CommandItem[] = (props.data?.items ?? []).map((p) => ({
      id: `property:${p.id}`,
      label: `${p.reference}  ${p.title ?? ""}`,
      keywords: `${p.reference} ${p.locality ?? ""} ${p.title ?? ""}`,
      hint: p.locality ?? undefined,
      icon: <BuildingsIcon aria-hidden />,
      group: t("shell.command_group_properties"),
    }));
    const can = (p: Permission) => Boolean(member?.permissions.includes(p));
    const actions: CommandItem[] = [
      ...(can("property.write") ? [{ id: "action:new-property", label: t("shell.action_new_property"), icon: <PlusIcon aria-hidden />, group: t("shell.command_group_actions") }] : []),
      ...(can("site.publish") ? [{ id: "action:publish", label: t("shell.action_publish"), icon: <RocketLaunchIcon aria-hidden />, group: t("shell.command_group_actions") }] : []),
      { id: "action:theme", label: t("shell.action_theme"), icon: <MoonIcon aria-hidden />, group: t("shell.command_group_actions") },
    ];
    return [...recents, ...found, ...pages, ...actions];
  }, [props.data, member, platformAdmin, me.tenants, t, recent, term]);

  const onAction = (id: string) => {
    if (id.startsWith("go:")) void navigate({ to: id.slice(3) });
    else if (id.startsWith("property:")) void navigate({ to: "/properties/$id", params: { id: id.slice(9) } });
    else if (id === "action:new-property") void navigate({ to: "/properties", search: { create: true } as never });
    else if (id === "action:publish") void navigate({ to: "/website" });
    else if (id === "action:theme") applyTheme(effectiveTheme() === "dark" ? "light" : "dark");
  };

  return (
    <CommandPalette
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      items={items}
      onAction={onAction}
      label={t("shell.search_label")}
      placeholder={t("shell.command_placeholder")}
      inputValue={input}
      onInputChange={setInput}
      emptyText={t("shell.command_empty")}
      footer={
        <>
          <span>
            <Kbd>↵</Kbd> {t("shell.command_hint_open")}
          </span>
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> {t("shell.command_hint_move")}
          </span>
          <span>
            <Kbd>Esc</Kbd> {t("shell.command_hint_close")}
          </span>
        </>
      }
    />
  );
}

function ShortcutsDialog({ isOpen, onOpenChange, items }: { isOpen: boolean; onOpenChange: (o: boolean) => void; items: NavItem[] }) {
  const { t } = useI18n();
  return (
    <UiDialog isOpen={isOpen} onOpenChange={onOpenChange} title={t("shell.shortcuts")} description={t("shell.shortcuts_lead")} closeLabel={t("common.close")}>
      <dl className="shortcuts">
        <div>
          <dt>{t("shell.shortcut_search")}</dt>
          <dd>
            <Kbd>/</Kbd>
          </dd>
        </div>
        <div>
          <dt>{t("shell.shortcut_help")}</dt>
          <dd>
            <Kbd>?</Kbd>
          </dd>
        </div>
        {items.map((i) => (
          <div key={i.to}>
            <dt>{t("shell.shortcut_go", { page: t(i.labelKey) })}</dt>
            <dd>
              <Kbd>G</Kbd> <span className="subtle">{t("shell.shortcut_then")}</span> <Kbd>{i.shortcut!.toUpperCase()}</Kbd>
            </dd>
          </div>
        ))}
      </dl>
    </UiDialog>
  );
}

/** Dunne lijn onder de topbalk zolang er iets ververst; verschijnt pas na 150ms om flikkeren te voorkomen. */
function FetchLine() {
  const fetching = useIsFetching() > 0;
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!fetching) return setShow(false);
    const id = window.setTimeout(() => setShow(true), 150);
    return () => window.clearTimeout(id);
  }, [fetching]);
  return <span className="fetch-line" data-active={show || undefined} aria-hidden />;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { me, member } = useSessionState();
  const platformAdmin = useIsPlatformAdmin();
  const platformMode = usePlatformMode();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const area = navItemFor(path);
  const navigate = useNavigate();
  const [commandOpen, setCommandOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const isDemo = !platformMode && me.tenants?.find((x) => x.id === member?.tenantId)?.is_demo;
  const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
  const shortcutItems = useMemo(() => availableNav(member, platformAdmin, Boolean(me.tenants?.some((x) => x.slug === "bres"))).filter((i) => i.shortcut), [member, platformAdmin, me.tenants]);

  useLayoutEffect(() => {
    document.documentElement.dataset.mode = platformMode ? "platform" : "tenant";
  }, [platformMode]);

  useEffect(() => {
    let pendingG = 0;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommandOpen((o) => !o);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (e.key === "/") {
        e.preventDefault();
        setCommandOpen(true);
      } else if (e.key === "?") {
        e.preventDefault();
        setHelpOpen(true);
      } else if (e.key.toLowerCase() === "g") {
        pendingG = performance.now();
      } else if (pendingG && performance.now() - pendingG < 1200) {
        const item = shortcutItems.find((i) => i.shortcut === e.key.toLowerCase());
        pendingG = 0;
        if (item) {
          e.preventDefault();
          void navigate({ to: item.to });
        }
      }
    };
    const onScroll = () => setScrolled((sheetRef.current?.scrollTop ?? 0) > 4 || window.scrollY > 4);
    const sheet = sheetRef.current;
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { passive: true });
    sheet?.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
      sheet?.removeEventListener("scroll", onScroll);
    };
  }, [navigate, shortcutItems]);

  return (
    <div className="app">
      <aside className="sidebar">
        <NavPanel />
      </aside>
      <ModalOverlay className="nav-sheet-overlay" isOpen={navOpen} onOpenChange={setNavOpen} isDismissable>
        <Modal className="nav-sheet">
          <Dialog className="ui-dialog" aria-label={t("nav.label")}>
            <NavPanel onNavigate={() => setNavOpen(false)} />
          </Dialog>
        </Modal>
      </ModalOverlay>
      <div className="sheet" id="sheet" ref={sheetRef} data-scroll-restoration-id="sheet">
        <header className="topbar" data-scrolled={scrolled || undefined}>
          <Button className="topbar__menu" variant="ghost" iconOnly aria-label={t("nav.open_menu")} icon={<ListIcon aria-hidden />} onPress={() => setNavOpen(true)} />
          <AriaButton className="search-trigger" onPress={() => setCommandOpen(true)} aria-label={t("shell.search_label")}>
            <MagnifyingGlassIcon aria-hidden />
            <span className="search-trigger__text">{t("shell.search")}</span>
            <span className="search-trigger__keys" aria-hidden>
              <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
              <Kbd>K</Kbd>
            </span>
          </AriaButton>
          <div className="topbar__end">
            {isDemo ? (
              <Tooltip content={t("shell.demo_hint")}>
                <AriaButton className="demo-chip">
                  <span className="demo-chip__dot" aria-hidden />
                  {t("shell.demo")}
                </AriaButton>
              </Tooltip>
            ) : null}
            <Tooltip content={t("shell.shortcuts")}>
              <AriaButton className="topbar__icon" aria-label={t("shell.shortcuts")} onPress={() => setHelpOpen(true)}>
                <KeyboardIcon aria-hidden />
              </AriaButton>
            </Tooltip>
            <ThemeSwitch />
          </div>
          <FetchLine />
        </header>
        <main className="content" id="main">
          <PageMarkProvider value={area ? { icon: area.icon, hue: area.hue } : null}>{children}</PageMarkProvider>
        </main>
      </div>
      <CommandMenu isOpen={commandOpen} onOpenChange={setCommandOpen} />
      <ShortcutsDialog isOpen={helpOpen} onOpenChange={setHelpOpen} items={shortcutItems} />
      <Toaster closeLabel={t("common.close")} regionLabel={t("shell.toasts")} />
    </div>
  );
}
